// How old the market a card priced from is, and whether that age matters.
//
// The app's edge is time: the frozen line against where the books are *now*.
// A market that is three hours old when the decision closes is not "now", and
// before this the card printed it exactly like a three-minute-old one. This
// module decides the one line of copy that says which it is. Pure, so the
// rule is tested here rather than by loading the page.
//
// `marketAge` dates one game; `slateMarketAge` folds those into the single
// line Home prints under the Week heading. Three per-game states:
//   - "live"    before the decision deadline: "as of 12:52pm"
//   - "stale"   inside the last STALE_WINDOW_MS before the deadline, with the
//               newest used quote older than STALE_AGE_MS: "1h 10m old"
//   - "closing" once the deadline has passed: the market is a closing line,
//               cut at `marketFrozenAt` (the server's cutoff)
// and null when there is no market to date at all.

const EASTERN = "America/New_York";

// A quote older than this, read inside the window below, means a poll was
// probably missed. The odds TTL is 10 minutes in the last hour before the
// Sunday deadline, so 45 minutes is several polls, not one late one.
export const STALE_AGE_MS = 45 * 60 * 1000;
export const STALE_WINDOW_MS = 60 * 60 * 1000;

// Pool deadline hour, Eastern -- Game::DEADLINE_HOUR.
const DEADLINE_HOUR = 13;

function toMs(value) {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function easternParts(ms) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    weekday: "short",
  }).formatToParts(new Date(ms));
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
    second: Number(get("second")),
    weekday: get("weekday"),
  };
}

// The instant a given Eastern wall-clock time happens. Reads the offset off
// the guess itself, so it follows the DST change the season crosses in
// November rather than hardcoding -4 or -5.
function easternInstant(year, month, day, hour) {
  const wall = Date.UTC(year, month - 1, day, hour);
  const seen = easternParts(wall);
  const offset =
    Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, seen.second) - wall;
  return wall - offset;
}

// Mirrors `Game#decision_deadline_at` -- the earlier of kickoff and that
// week's Sunday 1:00pm ET -- from the game's own kickoff alone, because the
// card is handed one game rather than the week.
//
// NFL weeks run Thursday (or a Wednesday opener) to Monday, with the odd
// Tuesday makeup, so the week's Sunday is the kickoff's own day, or one or two
// days before a Monday or Tuesday game. A game before that Sunday closes at
// its own kickoff, which is always the earlier of the two.
export function decisionDeadline(game) {
  const frozen = toMs(game?.marketFrozenAt);
  if (frozen != null) return frozen;

  const kickoff = toMs(game?.kickoffAt);
  if (kickoff == null) return null;

  const deadline = sundayDeadlineFor(kickoff);
  return deadline == null ? kickoff : Math.min(kickoff, deadline);
}

// The week's Sunday 1:00pm ET for a kickoff on that Sunday, or on the Monday
// or Tuesday after it -- `Game.sunday_deadline_for` read from one kickoff.
// Null for a Wednesday-to-Saturday kickoff (it closes at its own kickoff) and
// for a missing or unparseable time.
export function sundayDeadlineFor(kickoffAt) {
  const kickoff = toMs(kickoffAt);
  if (kickoff == null) return null;

  const et = easternParts(kickoff);
  const daysBack = { Sun: 0, Mon: 1, Tue: 2 }[et.weekday];
  if (daysBack == null) return null;

  // Date.UTC normalises a day of 0 or -1 into the previous month.
  const sunday = new Date(Date.UTC(et.year, et.month - 1, et.day - daysBack));
  return easternInstant(
    sunday.getUTCFullYear(),
    sunday.getUTCMonth() + 1,
    sunday.getUTCDate(),
    DEADLINE_HOUR
  );
}

// True for a kickoff on a Sunday, read in Eastern time.
export function isEasternSunday(kickoffAt) {
  const kickoff = toMs(kickoffAt);
  return kickoff != null && easternParts(kickoff).weekday === "Sun";
}

// Newest `capturedAt` among the books the card actually priced from. The
// server has already dropped stale and implausible rows from `spreads`, so
// every row here is one the model used.
export function newestQuoteAt(game) {
  const times = (game?.spreads || [])
    .map((book) => toMs(book.capturedAt))
    .filter((ms) => ms != null);
  return times.length ? Math.max(...times) : null;
}

// "12:52pm" in Eastern, with no zone label: the pool runs on Eastern.
export function formatEasternTime(ms) {
  const { hour, minute } = easternParts(ms);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, "0")}${hour < 12 ? "am" : "pm"}`;
}

// "52m", "1h 10m", "3h". Whole minutes, floored: a quote 44m59s old is 44m.
export function formatAge(ms) {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

export function marketAge(game, now = Date.now()) {
  const newest = newestQuoteAt(game);
  if (newest == null) return null;

  const nowMs = toMs(now);
  const deadline = decisionDeadline(game);

  // Closed: either the server cut the market at the deadline, or the clock
  // has passed it since the page loaded. Either way the number is a closing
  // line, and its time is the deadline, not the last quote.
  if (deadline != null && (game.marketFrozenAt != null || nowMs >= deadline)) {
    return {
      state: "closing",
      time: formatEasternTime(deadline),
      stale: false,
      title: "The books as of this game's pick deadline.",
    };
  }

  const age = nowMs - newest;
  const inWindow = deadline != null && deadline - nowMs <= STALE_WINDOW_MS;
  if (inWindow && age > STALE_AGE_MS) {
    return {
      state: "stale",
      time: formatEasternTime(newest),
      age: formatAge(age),
      stale: true,
      title:
        `The newest book quote is ${formatAge(age)} old with the pick deadline under an hour away. ` +
        "An odds poll may have been missed, so this market may not be where the books are now.",
    };
  }

  return {
    state: "live",
    time: formatEasternTime(newest),
    stale: false,
    title: "The newest quote among the books used for this game.",
  };
}

// One line for the whole slate, printed once under the Week heading on Home.
// The books are polled together, so every card would print the same time;
// saying it once keeps it out of the cards, where it read as part of the
// Market number it sat beside.
//
// Reads only games still to be played. While any of them is open:
//   - "Books 1h 10m old", amber, when any open game is stale -- the oldest
//     such age, since that is the card most at risk of being wrong
//   - "Books as of 12:52pm" otherwise, the newest quote across the open games
// Once every remaining game has closed: "Closing lines · 1:00pm", at the
// latest of their deadlines. null when there is no market at all.
export function slateMarketAge(games, now = Date.now()) {
  const nowMs = toMs(now);
  const priced = (games || [])
    .filter((game) => !game.completed)
    .map((game) => ({ game, age: marketAge(game, nowMs) }))
    .filter(({ age }) => age != null);
  if (!priced.length) return null;

  const open = priced.filter(({ age }) => age.state !== "closing");
  if (!open.length) {
    const deadline = Math.max(...priced.map(({ game }) => decisionDeadline(game)));
    return {
      state: "closing",
      label: `Closing lines · ${formatEasternTime(deadline)}`,
      stale: false,
      title: "Every remaining game is past its pick deadline; the books shown are as of that deadline.",
    };
  }

  const stale = open.filter(({ age }) => age.stale);
  if (stale.length) {
    const oldest = Math.min(...stale.map(({ game }) => newestQuoteAt(game)));
    const age = formatAge(nowMs - oldest);
    return {
      state: "stale",
      label: `Books ${age} old`,
      stale: true,
      title:
        `The newest quote on ${stale.length === 1 ? "one game" : `${stale.length} games`} is ${age} old ` +
        "with its pick deadline under an hour away. An odds poll may have been missed.",
    };
  }

  const newest = Math.max(...open.map(({ game }) => newestQuoteAt(game)));
  return {
    state: "live",
    label: `Books as of ${formatEasternTime(newest)}`,
    stale: false,
    title: "The newest book quote across the games still open.",
  };
}

// "2h", "45m", "3d" -- the coarse age the Books panel prints per row.
function coarseAge(ms) {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// One book row's age in the Books panel.
//
// Before the game's decision deadline it is measured from now: "Updated 4m
// ago". After it, the quote is a closing line (the server only sends quotes
// at or before the cutoff), so it is measured from the cutoff instead: "at
// close", or "2h before close". Measured from now, a closed week's books all
// read "Updated 5d ago", which says nothing about the quote and makes a
// perfectly good closing line look abandoned.
//
// Both are timed by when a poll last SAW the quote (`lastSeenAt`), not by the
// book's own `capturedAt` stamp: Novig and ProphetX move that only when their
// number changes, so a quote live at the cutoff read "1d before close". Same
// rule the server's staleness test uses (`MarketLine#last_seen`), with the
// same fallback to `capturedAt` for rows from before the column.
export function bookQuoteAge(book, game, now = Date.now()) {
  const seen = toMs(book?.lastSeenAt) ?? toMs(book?.capturedAt);
  const nowMs = toMs(now);
  if (seen == null || nowMs == null) return "Update time unavailable";

  const deadline = decisionDeadline(game);
  if (deadline != null && (game?.marketFrozenAt != null || nowMs >= deadline)) {
    // Seen at or after the cutoff: that exact quote was on offer at the close.
    const before = deadline - seen;
    if (before < 60000) return "at close";
    return `${coarseAge(before)} before close`;
  }

  const elapsed = nowMs - seen;
  if (elapsed < 60000) return "Updated just now";
  return `Updated ${coarseAge(elapsed)} ago`;
}

// The pool's own deadline for a slate: the latest decision deadline among its
// games, which is that week's Sunday 1:00pm ET (a Monday game is capped there).
// Games played earlier close at their own kickoff and never move it. Null when
// no game has a kickoff yet.
export function poolDeadline(games) {
  const deadlines = (games || []).map(decisionDeadline).filter((ms) => ms != null);
  return deadlines.length ? Math.max(...deadlines) : null;
}

// "Sun 1:00 PM", Eastern: the pool runs on Eastern.
export function formatDeadline(ms) {
  const { weekday, hour, minute } = easternParts(ms);
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${weekday} ${h12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

// "1d 15h left", "3h 20m left", "12m left", then "Closed". Floored, so it never
// promises a minute that is not there.
export function deadlineCountdown(deadline, now = Date.now()) {
  const left = toMs(deadline) - toMs(now);
  if (!(left > 0)) return "Closed";
  const minutes = Math.floor(left / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
}
